/**
 * Lightweight cold-load instrumentation for Phase 0 baseline measurement.
 * Enable via `featureTileFetcher({ loadMetrics: true })` or
 * `window.__featuresLoadMetrics?.enable()` in DevTools.
 *
 * Filter Network tab by `[FeaturesService] request (per-layer)` to cross-check
 * `perLayerRequestCount`. Compare layers × jobs × tiles against this summary.
 */

export interface FeaturesLoadMetricsSnapshot {
  readonly perLayerRequestCount: number;
  readonly combinedRequestCount: number;
  readonly uniqueTileCount: number;
  readonly layerCount: number;
  readonly jobCount: number;
  readonly startedAt: number | null;
  readonly elapsedMs: number | null;
}

interface TileKey {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

const tileKey = (x: number, y: number, z: number): string => `${z}/${x}/${y}`;

class FeaturesLoadMetricsTracker {
  private perLayerRequestCount = 0;
  private combinedRequestCount = 0;
  private readonly uniqueTiles = new Set<string>();
  private layerCount = 0;
  private jobCount = 0;
  private startedAt: number | null = null;
  private enabled = false;

  public enable(): void {
    this.enabled = true;
    this.reset();
  }

  public disable(): void {
    this.enabled = false;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public reset(): void {
    this.perLayerRequestCount = 0;
    this.combinedRequestCount = 0;
    this.uniqueTiles.clear();
    this.layerCount = 0;
    this.jobCount = 0;
    this.startedAt = performance.now();
  }

  public recordVisibility(
    layerIds: readonly string[],
    jobIds: readonly string[] | undefined
  ): void {
    if (!this.enabled) {
      return;
    }

    this.layerCount = layerIds.length;
    this.jobCount = jobIds?.length ?? 0;
  }

  public recordRequest(
    mode: 'per-layer' | 'combined',
    tile: TileKey,
    layerCount: number,
    jobCount: number
  ): void {
    if (!this.enabled) {
      return;
    }

    if (this.startedAt === null) {
      this.startedAt = performance.now();
    }

    this.uniqueTiles.add(tileKey(tile.x, tile.y, tile.z));
    this.layerCount = Math.max(this.layerCount, layerCount);
    this.jobCount = Math.max(this.jobCount, jobCount);

    if (mode === 'per-layer') {
      this.perLayerRequestCount += 1;
    } else {
      this.combinedRequestCount += 1;
    }
  }

  public getSnapshot(): FeaturesLoadMetricsSnapshot {
    const elapsedMs =
      this.startedAt !== null ? performance.now() - this.startedAt : null;

    return {
      perLayerRequestCount: this.perLayerRequestCount,
      combinedRequestCount: this.combinedRequestCount,
      uniqueTileCount: this.uniqueTiles.size,
      layerCount: this.layerCount,
      jobCount: this.jobCount,
      startedAt: this.startedAt,
      elapsedMs,
    };
  }

  public formatSummary(): string {
    const snapshot = this.getSnapshot();
    const legacyEstimate =
      snapshot.layerCount * snapshot.jobCount * snapshot.uniqueTileCount;

    return (
      `[FeaturesLoadMetrics] per-layer=${snapshot.perLayerRequestCount}, ` +
      `combined=${snapshot.combinedRequestCount}, ` +
      `tiles=${snapshot.uniqueTileCount}, ` +
      `layers=${snapshot.layerCount}, jobs=${snapshot.jobCount}, ` +
      `elapsedMs=${snapshot.elapsedMs?.toFixed(0) ?? 'n/a'}, ` +
      `legacyLayer×Job×Tile estimate=${legacyEstimate}`
    );
  }
}

export const featuresLoadMetrics = new FeaturesLoadMetricsTracker();

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- DevTools debug global
    __featuresLoadMetrics?: FeaturesLoadMetricsTracker;
  }
}

if (typeof window !== 'undefined') {
  window.__featuresLoadMetrics = featuresLoadMetrics;
}
